import { OpsSeverity, OpsSignalKind, SignalNature } from '@navio/nsdk';
import type { NoddiIconName } from 'noddi-ui-common';
import type { OpsSignal } from './opsSignal';

export type KindTone = 'blue' | 'purple' | 'coral' | 'green';

export interface KindMeta {
  title: string;
  description: string;
  icon: NoddiIconName;
  tone: KindTone;
}

export const OPS_KIND_META: Record<OpsSignalKind, KindMeta> = {
  [OpsSignalKind.ROUTE_BEHIND_SCHEDULE]: {
    title: 'Route delay',
    description: 'Running late, or not started on time',
    icon: 'Route',
    tone: 'blue'
  },
  [OpsSignalKind.ROUTE_UNCONFIRMED]: {
    title: 'Route not confirmed',
    description: 'Selected routes waiting for the worker to confirm',
    icon: 'Bell',
    tone: 'blue'
  },
  [OpsSignalKind.SOLUTION_NOT_SELECTED]: {
    title: 'Route planning',
    description: 'Route planning without a selected solution',
    icon: 'Map',
    tone: 'purple'
  },
  [OpsSignalKind.ROUTE_PLANNER_FAILED]: {
    title: 'Route planning failed',
    description: 'Route planning jobs that failed',
    icon: 'TriangleAlert',
    tone: 'purple'
  },
  [OpsSignalKind.INFEASIBLE_ROUTE_PLAN]: {
    title: 'No valid routes found',
    description: 'Newest planning run found no valid routes for the day',
    icon: 'TriangleAlert',
    tone: 'purple'
  },
  [OpsSignalKind.FLEET_AUTO_BOOKING_BLOCKED]: {
    title: 'Auto-booking blocked',
    description: 'Cars on a fleet schedule the auto-booker could not book',
    icon: 'CalendarOff',
    tone: 'coral'
  },
  [OpsSignalKind.UNABLE_TO_COMPLETE]: {
    title: 'Unable to complete',
    description: 'Bookings marked unable to complete',
    icon: 'CircleX',
    tone: 'coral'
  },
  [OpsSignalKind.PAST_BOOKING_OPEN]: {
    title: 'Booking not completed',
    description: 'Booked before today, still confirmed or on a route',
    icon: 'CalendarX',
    tone: 'coral'
  },
  [OpsSignalKind.NO_WORKER_SHIFT]: {
    title: 'No matching worker shift',
    description: 'Confirmed booking with no worker shift for the delivery window',
    icon: 'UserX',
    tone: 'coral'
  },
  [OpsSignalKind.DEDICATED_WORKER_NOT_ON_SHIFT]: {
    title: 'Dedicated worker not scheduled',
    description: "None of the customer's dedicated workers has a shift that day, so any worker can get it",
    icon: 'UserCheck',
    tone: 'coral'
  },
  [OpsSignalKind.MOUNTING_OVERDUE]: {
    title: 'Tires waiting to be mounted',
    description: 'Tires received and not mounted in time',
    icon: 'Wrench',
    tone: 'green'
  },
  [OpsSignalKind.TIRE_OFFER_NOT_CREATED]: {
    title: 'Tire offer not created',
    description: 'Low tread was measured and no tire offer was drafted',
    icon: 'TriangleAlert',
    tone: 'coral'
  },
  [OpsSignalKind.TIRE_ORDER_FAILED]: {
    title: 'Failed tire order',
    description: 'Tire order the supplier did not fulfill',
    icon: 'CircleX',
    tone: 'coral'
  },
  [OpsSignalKind.WHEEL_STORAGE_ISSUE]: {
    title: 'Wheel storage issue',
    description: 'Membership and custody disagree',
    icon: 'Hotel',
    tone: 'green'
  },
  [OpsSignalKind.WHEEL_SET_WITHOUT_LOCATION]: {
    title: 'Wheel set not on a storage unit',
    description: 'At the facility, not placed on a storage unit',
    icon: 'Package',
    tone: 'green'
  },
  [OpsSignalKind.WHEEL_SET_NOT_MOVED]: {
    title: 'Wheel set not moved',
    description: 'At the facility, untouched for longer than the chosen time',
    icon: 'PackageSearch',
    tone: 'green'
  },
  [OpsSignalKind.TRANSFER_ORDER_NEEDED]: {
    title: 'Transfer order needed',
    description: 'Wheel sets to move before a scheduled booking',
    icon: 'Truck',
    tone: 'green'
  },
  [OpsSignalKind.TRANSFER_ORDER_STUCK]: {
    title: 'Transfer order with problems',
    description: 'Wheel sets not moved, a reported problem, or late delivery',
    icon: 'CircleX',
    tone: 'green'
  },
  [OpsSignalKind.ABSENCE_REQUEST]: {
    title: 'Absence request',
    description: 'Pending requests awaiting approval',
    icon: 'CalendarClock',
    tone: 'purple'
  },
  [OpsSignalKind.OVERTIME_OR_CORRECTION]: {
    title: 'Overtime and corrections',
    description: 'Pending overtime and time-log corrections',
    icon: 'Clock',
    tone: 'purple'
  },
  [OpsSignalKind.MISSING_WHEEL_SPECS]: {
    title: 'Missing tire pressure or torque',
    description: 'Cars booked in the next week without catalog tire pressure or wheel nut torque',
    icon: 'Wrench',
    tone: 'coral'
  }
};

const WHEEL_STORAGE_ISSUES_SECTION = 'wheel-storage-issues';

const WHEEL_STORAGE_ISSUE_KINDS: readonly OpsSignalKind[] = [
  OpsSignalKind.WHEEL_STORAGE_ISSUE,
  OpsSignalKind.WHEEL_SET_WITHOUT_LOCATION,
  OpsSignalKind.WHEEL_SET_NOT_MOVED
];

const WHEEL_STORAGE_ISSUES_META: KindMeta = {
  title: 'Wheel storage issues',
  description:
    'Membership and custody disagree, or a wheel set at the facility is off its storage unit or untouched for a long time',
  icon: 'Hotel',
  tone: 'green'
};

// Not the "Routes today" day card.
const ROUTE_SIGNALS_SECTION = 'route-signals';

// Not the "Route planning tomorrow" day card.
const PLANNING_SIGNALS_SECTION = 'planning-signals';

export interface KindSection {
  id: OpsListSectionId;
  kinds: readonly OpsSignalKind[];
  meta: KindMeta;
}

const GROUPED_SECTIONS: readonly KindSection[] = [
  {
    id: ROUTE_SIGNALS_SECTION,
    kinds: [OpsSignalKind.ROUTE_BEHIND_SCHEDULE, OpsSignalKind.ROUTE_UNCONFIRMED],
    meta: {
      title: 'Routes',
      description: 'Running late, not started, or not confirmed by the worker',
      icon: 'Route',
      tone: 'blue'
    }
  },
  {
    id: PLANNING_SIGNALS_SECTION,
    kinds: [
      OpsSignalKind.SOLUTION_NOT_SELECTED,
      OpsSignalKind.ROUTE_PLANNER_FAILED,
      OpsSignalKind.INFEASIBLE_ROUTE_PLAN
    ],
    meta: {
      title: 'Route planning',
      description: 'No solution selected, planning failed, or no valid routes found',
      icon: 'Map',
      tone: 'purple'
    }
  },
  {
    id: WHEEL_STORAGE_ISSUES_SECTION,
    kinds: WHEEL_STORAGE_ISSUE_KINDS,
    meta: WHEEL_STORAGE_ISSUES_META
  }
];

const LIST_SECTIONS: readonly KindSection[] = GROUPED_SECTIONS;

export type OpsListSectionId =
  | OpsSignalKind
  | typeof ROUTE_SIGNALS_SECTION
  | typeof PLANNING_SIGNALS_SECTION
  | typeof WHEEL_STORAGE_ISSUES_SECTION;

export const sectionForKind = (kind: OpsSignalKind): KindSection => {
  const grouped: KindSection | undefined = GROUPED_SECTIONS.find((section) => section.kinds.includes(kind));
  if (grouped) {
    return grouped;
  }
  return {
    id: kind,
    kinds: [kind],
    meta: OPS_KIND_META[kind]
  };
};

export const sectionForSignal = (signal: OpsSignal): KindSection => sectionForKind(signal.kind);

export const sectionFromParam = (value: string | null | undefined): KindSection | null => {
  if (value == null || value === '') {
    return null;
  }
  const listed: KindSection | undefined = LIST_SECTIONS.find((section) => section.id === value);
  if (listed) {
    return listed;
  }
  if (!isOpsSignalKind(value)) {
    return null;
  }
  return sectionForKind(value);
};

export const categoryMeta = (signal: OpsSignal): KindMeta => sectionForSignal(signal).meta;

export const signalInSection = (signal: OpsSignal, section: KindSection): boolean =>
  section.kinds.includes(signal.kind);

export const PARTNER_KINDS: OpsSignalKind[] = Object.values(OpsSignalKind);

export const OPS_NATURES: { nature: SignalNature; label: string; icon: NoddiIconName }[] = [
  { nature: SignalNature.PROBLEM, label: 'Problems', icon: 'CircleX' },
  { nature: SignalNature.TO_DO, label: 'To do', icon: 'ClipboardList' },
  { nature: SignalNature.FOLLOW_UP, label: 'Follow-ups', icon: 'Flag' }
];

export const OPS_NATURE_BY_KIND: Record<OpsSignalKind, SignalNature> = {
  [OpsSignalKind.ROUTE_BEHIND_SCHEDULE]: SignalNature.PROBLEM,
  [OpsSignalKind.ROUTE_UNCONFIRMED]: SignalNature.TO_DO,
  [OpsSignalKind.SOLUTION_NOT_SELECTED]: SignalNature.TO_DO,
  [OpsSignalKind.ROUTE_PLANNER_FAILED]: SignalNature.PROBLEM,
  [OpsSignalKind.INFEASIBLE_ROUTE_PLAN]: SignalNature.PROBLEM,
  [OpsSignalKind.FLEET_AUTO_BOOKING_BLOCKED]: SignalNature.PROBLEM,
  [OpsSignalKind.UNABLE_TO_COMPLETE]: SignalNature.FOLLOW_UP,
  [OpsSignalKind.PAST_BOOKING_OPEN]: SignalNature.PROBLEM,
  [OpsSignalKind.NO_WORKER_SHIFT]: SignalNature.PROBLEM,
  [OpsSignalKind.DEDICATED_WORKER_NOT_ON_SHIFT]: SignalNature.PROBLEM,
  [OpsSignalKind.MOUNTING_OVERDUE]: SignalNature.TO_DO,
  [OpsSignalKind.TIRE_OFFER_NOT_CREATED]: SignalNature.PROBLEM,
  [OpsSignalKind.TIRE_ORDER_FAILED]: SignalNature.PROBLEM,
  [OpsSignalKind.WHEEL_STORAGE_ISSUE]: SignalNature.PROBLEM,
  [OpsSignalKind.WHEEL_SET_WITHOUT_LOCATION]: SignalNature.PROBLEM,
  [OpsSignalKind.WHEEL_SET_NOT_MOVED]: SignalNature.PROBLEM,
  [OpsSignalKind.TRANSFER_ORDER_NEEDED]: SignalNature.TO_DO,
  [OpsSignalKind.TRANSFER_ORDER_STUCK]: SignalNature.FOLLOW_UP,
  [OpsSignalKind.ABSENCE_REQUEST]: SignalNature.TO_DO,
  [OpsSignalKind.OVERTIME_OR_CORRECTION]: SignalNature.TO_DO,
  [OpsSignalKind.MISSING_WHEEL_SPECS]: SignalNature.TO_DO
};

export const isOpsSignalKind = (value: string | null | undefined): value is OpsSignalKind =>
  value != null && Object.hasOwn(OPS_KIND_META, value);

export const isSignalOverdue = (signal: OpsSignal, now: Date): boolean =>
  signal.nature === SignalNature.TO_DO && signal.impactAt != null && new Date(signal.impactAt) < now;

export const isSignalCritical = (signal: OpsSignal): boolean =>
  signal.nature === SignalNature.PROBLEM && signal.severity === OpsSeverity.CRITICAL;

export const countSignalsByKind = (signals: OpsSignal[]): Map<OpsSignalKind, number> => {
  const counts = new Map<OpsSignalKind, number>();
  signals.forEach((signal) => counts.set(signal.kind, (counts.get(signal.kind) ?? 0) + 1));
  return counts;
};
